/**
 * @render react
 * @name TeamHub
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqid from 'uniqid';

import { withPolaris } from 'Components/protons/Polaris';

import { Icon } from 'Components/atoms/Icon';
import { Image } from 'Components/atoms/Image';
import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * TeamHub component
 * @param {string} title - title of the component
 * @param {string} titlePosition - position of the title. Can be either before-name or after-name
 * @param {number} headingSize - size of the Heading
 * @param {string} imageSize - size of the image
 * @param {array} content - list of team members
 * @param {string} summary - Can be used to display legal text
 * @param {boolean} lazyLoad - Image lazyLoad
 * @param {string} linkLabel - Prefix or link label
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {boolean} showLatestArticles - Show latest articles for each member. Defauls to false
 */
const TeamHub = (
  {
    title,
    titlePosition,
    headingSize,
    imageSize,
    lazyLoad,
    linkLabel,
    linkIcon,
    content,
    summary,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
    showLatestArticles,
  },
) => (
  <div
    className={classNames({
      [`${cssPrefix}__teamhub`]: loadStyles,
      ...extraClassNames,
    })}
  >
    {title && (
      <div className={`${cssPrefix}__teamhub--header`}>
        <Heading
          size={headingSize}
          extraClassNames={{ [`${cssPrefix}__teamhub--title`]: true }}
        >
          {title}
        </Heading>
      </div>
    )}

    {content.length > 0 && (
      <div className={`${cssPrefix}__teamhub--content`}>
        {content.map(member => (
          <div
            className={classNames({
              [`${cssPrefix}__teamhub--member`]: true,
              ...member.extraClassNames,
            })}
            key={uniqid()}
          >

            {(member.url && !member.hasNameAsLink) && (
              <Link
                href={member.url}
                extraClassNames={{
                  [`${cssPrefix}__teamhub--member-url`]: true,
                }}
              />
            )}

            {member.photo && (
              <div className={`${cssPrefix}__teamhub--member-photo`}>
                <Image
                  src={member.photo}
                  width={member.width}
                  height={member.height}
                  alt={member.name}
                  size={imageSize}
                  lazyLoad={lazyLoad}
                />
              </div>
            )}

            <div className={`${cssPrefix}__teamhub--member-content`}>
              {member.title && titlePosition === 'before-name' && (
                <p className={`${cssPrefix}__teamhub--member-title`}>
                  {member.title}
                </p>
              )}

              <Heading
                size={headingSize - 1}
                extraClassNames={{
                  [`${cssPrefix}__teamhub--member-name`]: true,
                }}
              >
                {member.url && member.hasNameAsLink
                  ? (
                    <Link
                      href={member.url}
                      extraClassNames={{
                        [`${cssPrefix}__teamhub--member-url`]: true,
                      }}
                    >
                      {member.name}
                    </Link>
                  )
                  : member.name}
              </Heading>

              {member.title && titlePosition === 'after-name' && (
                <p className={`${cssPrefix}__teamhub--member-title`}>
                  {member.title}
                </p>
              )}

              {member.bio && (
                <div className={`${cssPrefix}__teamhub--member-bio`}>
                  {member.bio}
                </div>
              )}

              {showLatestArticles && Array.isArray(member.latest) && member.latest.length > 0 && (
                <div className={`${cssPrefix}__teamhub--member-latest`}>
                  <p>Latest Articles</p>
                  {member.latest.map(latest => <Link href={latest.url} key={latest.url}>{latest.title}</Link>)}
                </div>
              )}

              {linkLabel && (
                <div className={`${cssPrefix}__teamhub--member-link`}>
                  <span>
                    {linkLabel}

                    {linkIcon
                      ? (<Icon icon={`${cssPrefix}__${linkIcon}`} />)
                      : ' ▶️'}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )}

    {summary && (
      <div className={`${cssPrefix}__teamhub--summary`}>
        {summary}
      </div>
    )}
  </div>
);

/**
 * Valid props
 */
TeamHub.propTypes = {
  title: PropTypes.string,
  titlePosition: PropTypes.string,
  headingSize: PropTypes.number,
  imageSize: PropTypes.string,
  content: PropTypes.arrayOf(PropTypes.shape({
    iconPath: PropTypes.string,
    url: PropTypes.string,
    hasNameAsLink: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    size: PropTypes.string,
    latest: PropTypes.shape(),
  })),
  lazyLoad: PropTypes.bool,
  linkLabel: PropTypes.string,
  linkIcon: PropTypes.string,
  summary: PropTypes.oneOfType([
    PropTypes.shape({
      html: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  showLatestArticles: PropTypes.bool,
};

/**
 * Default props
 */
TeamHub.defaultProps = {
  title: '',
  titlePosition: 'before-name',
  headingSize: 3,
  imageSize: 'author',
  content: [],
  linkLabel: 'View all articles',
  linkIcon: null,
  summary: null,
  lazyLoad: true,
  loadStyles: true,
  extraClassNames: {},
  context: {},
  showLatestArticles: false,
};

export default withPolaris(TeamHub);
